import React, { ReactElement, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Event } from 'services/api';
import { OrganizationSummary } from 'services/api/models';
import { Image } from 'components/lib';
import {
  COLORS,
  FONT,
  MaterialSymbol,
  SkeletonBlock,
  YellowCtaButton,
  PICKED_CARD_WIDTH,
  PICKED_IMAGE_HEIGHT,
  formatLongDate,
  formatTimeRange,
  isRsvpClosed,
} from '../../shared';

const HOST_LOGO_SIZE = 50;
const MAX_VISIBLE_LOGOS = 2;
const VIEW_DETAILS_WIDTH = 110;

const HostLogo = ({
  org,
  onClick,
}: {
  org: OrganizationSummary;
  onClick: () => void;
}): ReactElement => {
  const [error, setError] = useState(false);
  return (
    <ButtonBase
      onClick={onClick}
      disableRipple
      aria-label={`View ${org.name}`}
      sx={{
        width: `${HOST_LOGO_SIZE}px`,
        height: `${HOST_LOGO_SIZE}px`,
        borderRadius: '8px',
        backgroundColor: COLORS.imagePlaceholder,
        overflow: 'hidden',
        flexShrink: 0,
        '&:focus-visible': {
          outline: `2px solid ${COLORS.bodyDark}`,
          outlineOffset: '2px',
        },
      }}
    >
      {org.logoUrl && !error ? (
        <Image
          src={org.logoUrl}
          alt={org.name}
          width={HOST_LOGO_SIZE}
          height={HOST_LOGO_SIZE}
          onError={() => setError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : null}
    </ButtonBase>
  );
};

export interface EventGridCardProps {
  event: Event;

  reserved?: boolean;
  checkedIn?: boolean;

  onCheckIn: () => void;
  checkInLabel?: string;

  onDetails: () => void;

  onEventClick?: () => void;

  onOrgClick?: (org: OrganizationSummary) => void;
}

export const EventGridCard = ({
  event,
  reserved = false,
  checkedIn = false,
  onCheckIn,
  checkInLabel = 'Check in',
  onDetails,
  onEventClick,
  onOrgClick,
}: EventGridCardProps): ReactElement => {
  const [heroError, setHeroError] = useState(false);
  const showHero = event.flyerImageUrl && !heroError;
  const handleEventClick = onEventClick ?? onDetails;

  const hosts = event.hostOrganizations ?? [];
  const visibleHosts =
    hosts.length <= MAX_VISIBLE_LOGOS
      ? hosts
      : [hosts.find(h => h.hostRole === 'PRIMARY') ?? hosts[0]];
  const overflow = hosts.length - visibleHosts.length;
  const overflowLabel = overflow > 99 ? '+99' : `+${overflow}`;
  const hasHosts = hosts.length > 0;
  const rsvpClosed = isRsvpClosed(event);

  // Reserved/checked-in events show the attendance CTA; everyone else sees the
  // discovery footer (host logos + View Details).
  const showAttendance = reserved || checkedIn;

  const handleOrgClick = (org: OrganizationSummary): void => {
    if (onOrgClick) onOrgClick(org);
  };

  return (
    <Box
      sx={{
        backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.cardBorder}`,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease, transform 200ms ease',
        '@media (hover: hover)': {
          '&:hover': {
            boxShadow: '0 8px 24px rgba(20,31,51,0.12)',
            transform: 'translateY(-2px)',
          },
        },
      }}
    >
      <ButtonBase
        onClick={handleEventClick}
        disableRipple
        aria-label={`View details for ${event.name}`}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${PICKED_CARD_WIDTH} / ${PICKED_IMAGE_HEIGHT}`,
          flexShrink: 0,
          backgroundColor: COLORS.imagePlaceholder,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: '-2px',
          },
        }}
      >
        {showHero ? (
          <>
            <Box aria-hidden sx={{ position: 'absolute', inset: 0 }}>
              <Image
                src={event.flyerImageUrl ?? ''}
                alt=""
                width={PICKED_CARD_WIDTH}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(18px)',
                  transform: 'scale(1.08)',
                }}
              />
            </Box>
            <Image
              src={event.flyerImageUrl ?? ''}
              alt={event.name}
              width={PICKED_CARD_WIDTH}
              onError={() => setHeroError(true)}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        ) : (
          <MaterialSymbol name="image" size={40} color={COLORS.muted} />
        )}
      </ButtonBase>

      <Box
        sx={{
          flex: '1 0 0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: '16px',
          gap: '16px',
        }}
      >
        <Box display="flex" flexDirection="column" gap="6px">
          <Typography
            component={ButtonBase}
            onClick={handleEventClick}
            disableRipple
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '22px',
              color: COLORS.headingDark,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': {
                outline: `2px solid ${COLORS.navy}`,
                outlineOffset: '2px',
              },
            }}
          >
            {event.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: '12px',
              color: COLORS.muted,
            }}
          >
            {formatLongDate(event.date)}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: '12px',
              color: COLORS.muted,
            }}
          >
            {formatTimeRange(event.startTime, event.endTime)}
          </Typography>
        </Box>

        {showAttendance ? (
          <Box display="flex" gap="16px" alignItems="center" width="100%">
            {checkedIn ? (
              <Box
                sx={{
                  flex: '1 0 0',
                  minWidth: 0,
                  backgroundColor: COLORS.scopeTrackBg,
                  display: 'flex',
                  gap: '6px',
                  height: '45px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: '14px',
                  py: '10px',
                  borderRadius: '8px',
                }}
              >
                <MaterialSymbol
                  name="check"
                  size={18}
                  color={COLORS.headingDark}
                  fill={1}
                />
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: '14px',
                    color: COLORS.headingDark,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Checked in
                </Typography>
              </Box>
            ) : (
              <ButtonBase
                onClick={onCheckIn}
                disableRipple
                aria-label={`${checkInLabel} to ${event.name}`}
                sx={{
                  flex: '1 0 0',
                  minWidth: 0,
                  backgroundColor: COLORS.ctaYellow,
                  gap: '6px',
                  height: '45px',
                  px: '14px',
                  py: '10px',
                  borderRadius: '8px',
                  color: COLORS.headingDark,
                  transition: 'background-color 150ms ease',
                  '&:hover': { backgroundColor: COLORS.ctaYellowHover },
                  '&:active': { backgroundColor: COLORS.ctaYellowActive },
                  '&:focus-visible': {
                    outline: `2px solid ${COLORS.headingDark}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <MaterialSymbol
                  name="qr_code_scanner"
                  size={18}
                  color={COLORS.headingDark}
                />
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: '14px',
                    color: COLORS.headingDark,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {checkInLabel}
                </Typography>
              </ButtonBase>
            )}
            <ButtonBase
              onClick={onDetails}
              disableRipple
              aria-label={`View details for ${event.name}`}
              sx={{
                flexShrink: 0,
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.cardBorder}`,
                height: '45px',
                px: '20px',
                py: '12px',
                borderRadius: '8px',
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '14px',
                color: COLORS.headingDark,
                whiteSpace: 'nowrap',
                transition: 'background-color 150ms ease',
                '&:hover': { backgroundColor: COLORS.hoverRow },
                '&:focus-visible': {
                  outline: `2px solid ${COLORS.navy}`,
                  outlineOffset: '-2px',
                },
              }}
            >
              Details
            </ButtonBase>
          </Box>
        ) : (
          <Box
            display="flex"
            gap="5px"
            alignItems="center"
            width="100%"
            justifyContent={hasHosts ? 'space-between' : 'flex-start'}
          >
            {hasHosts && (
              <Box
                display="flex"
                gap="6px"
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                {visibleHosts.map(org => (
                  <HostLogo
                    key={org.id}
                    org={org}
                    onClick={() => handleOrgClick(org)}
                  />
                ))}
                {overflow > 0 && (
                  <Box
                    sx={{
                      width: `${HOST_LOGO_SIZE}px`,
                      height: `${HOST_LOGO_SIZE}px`,
                      borderRadius: '8px',
                      backgroundColor: COLORS.skeleton,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: '11px',
                        color: COLORS.muted,
                      }}
                    >
                      {overflowLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {rsvpClosed ? (
              <Box
                sx={{
                  ...(hasHosts
                    ? { width: `${VIEW_DETAILS_WIDTH}px`, flexShrink: 0 }
                    : { flex: '1 0 0', minWidth: 0 }),
                  height: '45px',
                  borderRadius: '8px',
                  backgroundColor: COLORS.skeleton,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: '14px',
                    color: COLORS.muted,
                    whiteSpace: 'nowrap',
                  }}
                >
                  RSVP closed
                </Typography>
              </Box>
            ) : (
              <YellowCtaButton
                fullWidth={!hasHosts}
                height={45}
                onClick={onDetails}
                ariaLabel={`View details for ${event.name}`}
                sx={
                  hasHosts
                    ? { width: `${VIEW_DETAILS_WIDTH}px`, flexShrink: 0 }
                    : undefined
                }
              >
                View Details
              </YellowCtaButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const EventGridCardSkeleton = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.cardBorder}`,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100%',
    }}
  >
    <Box
      sx={{
        width: '100%',
        aspectRatio: `${PICKED_CARD_WIDTH} / ${PICKED_IMAGE_HEIGHT}`,
      }}
    >
      <SkeletonBlock width="100%" height="100%" />
    </Box>
    <Box p="16px" display="flex" flexDirection="column" gap="16px" flex="1 0 0">
      <Box display="flex" flexDirection="column" gap="8px">
        <SkeletonBlock width="85%" height={20} />
        <SkeletonBlock width="60%" height={14} />
        <SkeletonBlock width="50%" height={14} />
      </Box>
      <Box display="flex" gap="16px" alignItems="center" mt="auto">
        <SkeletonBlock width="100%" height={45} radius={8} />
        <SkeletonBlock
          width={81}
          height={45}
          radius={8}
          sx={{ flexShrink: 0 }}
        />
      </Box>
    </Box>
  </Box>
);
